// Theme preference → resolved colour scheme → <html class="dark">.
//
// The preference ('light' | 'dark' | 'system') lives in the settings store;
// 'system' follows `prefers-color-scheme`. The same resolution runs inline in
// index.html before first paint (keep the two in sync) so a dark-mode player
// never sees a light flash while the bundle loads.

export const THEME_PREFERENCES = ['system', 'light', 'dark'] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export type ResolvedTheme = 'light' | 'dark';

export const DARK_QUERY = '(prefers-color-scheme: dark)';

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEME_PREFERENCES as readonly string[]).includes(value);
}

/** Pure resolution: an explicit choice wins, 'system' defers to the OS. */
export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}

/** Whether the OS currently asks for dark. False when matchMedia is unavailable. */
export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DARK_QUERY).matches;
}

/** Reflect a resolved theme on the root element (class + data attribute + color-scheme). */
export function applyResolvedTheme(theme: ResolvedTheme, root: HTMLElement): void {
  root.classList.toggle('dark', theme === 'dark');
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
}
